<template>
  <v-app :class="selectedTimeSlot === null ? '' : 'grabbing'">
    <BusinessInfoModal />
    <div @mouseup="fixSelectedTimeSlot">
      <v-navigation-drawer width="300px" v-model="drawer" left clipped app>
        <keep-alive>
          <component :is="drawerComponent"></component>
        </keep-alive>
      </v-navigation-drawer>
      <v-app-bar
        :color="impersonate ? 'secondary' : 'primary'"
        dark
        app
        clipped-left
      >
        <v-app-bar-nav-icon
          @click="drawer = !drawer"
          :disabled="!hasDrawer"
        ></v-app-bar-nav-icon>

        <v-toolbar-title
          style="display: flex; align-items: center; column-gap: 0.5rem"
        >
          <img
            src="@/assets/cronos-white.png"
            style="width: 40px; height: auto"
            alt="Kronos logo"
          />
          <span>Kronos</span>
        </v-toolbar-title>

        <v-tabs
          v-model="tab"
          background-color="transparent"
          centered
          icons-and-text
          optional
          style="width: auto"
        >
          <v-tabs-slider></v-tabs-slider>
          <v-tab
            v-for="menuItem in menuItems"
            :to="menuItem.path"
            :key="menuItem.path"
          >
            {{ menuItem.name }}
            <v-icon>{{ menuItem.icon }}</v-icon>
          </v-tab>
        </v-tabs>

        <v-toolbar-items>
          <v-btn text to="/notification">
            <v-badge
              :content="notificationCount"
              :value="notificationCount"
              color="green"
              overlap
            >
              <v-icon>mdi-email</v-icon>
            </v-badge>
          </v-btn>
          <template v-for="item in topMenuItems">
            <v-tooltip bottom :key="item.name">
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" text @click="$refs[item.name].openModal()">
                  <v-icon>{{ item.icon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ item.description }}</span>
            </v-tooltip>
          </template>
          <v-btn text @click="editModalChooseUser()">
            <v-icon v-if="impersonate">mdi-incognito</v-icon>
            <v-icon v-else>mdi-account</v-icon>
            {{ customerFullName }}
          </v-btn>
          <v-btn
            v-if="hasAdminAccess"
            text
            icon
            :href="`${apiUrl}/goto-admin?token=${token}`"
          >
            <v-icon>mdi-cog-outline</v-icon>
          </v-btn>
          <v-btn text icon @click.stop="logout">
            <v-icon v-if="impersonate">mdi-incognito-off</v-icon>
            <v-icon v-else>mdi-power</v-icon>
          </v-btn>
        </v-toolbar-items>
        <CounterModal ref="counter"></CounterModal>
        <SilaeExportModal ref="silae"></SilaeExportModal>
        <OisoExportModal ref="oiso"></OisoExportModal>
        <DailyDistanceExportModal ref="rh-stat"></DailyDistanceExportModal>
        <WeightModal ref="weight"></WeightModal>
      </v-app-bar>
      <v-main>
        <router-view ref="calendarView"></router-view>
      </v-main>
    </div>
    <EventFormModal></EventFormModal>
    <ServiceEventModal></ServiceEventModal>
    <ChooseUserModal></ChooseUserModal>
    <NotificationModal></NotificationModal>
  </v-app>
</template>

<script>
import * as actions from "@/store/types";
import * as getters from "@/store/getters";
import { mapActions, mapState, mapGetters } from "vuex";
import { EventBus } from "@/tools/event-bus";
import {
  useInfoNotificationsCountQuery,
  useCritNotificationsQuery,
} from "@/composables/notification";
import BusinessInfoModal from "@/components/participant/modal/BusinessInfoModal.vue";

const EventListContainer = () =>
  import(/* webpackChunkName: "eventlist" */ "./EventListContainer.vue");
const CounterModal = () =>
  import(
    /* webpackChunkName: "counter" */ "@/components/participant/modal/CounterModal.vue"
  );
const WeightModal = () =>
  import(
    /* webpackChunkName: "weight" */ "@/components/participant/modal/WeightModal.vue"
  );
const SilaeExportModal = () =>
  import(
    /* webpackChunkName: "silae" */ "@/components/participant/modal/SilaeExportModal.vue"
  );
const DailyDistanceExportModal = () =>
  import(
    /* webpackChunkName: "rh-stat" */ "@/components/participant/modal/DailyDistanceExportModal.vue"
  );

const OisoExportModal = () =>
  import(
    /* webpackChunkName: "oiso" */ "@/components/participant/modal/OisoExportModal.vue"
  );
const EventFormModal = () =>
  import(
    /* webpackChunkName: "eventform" */ "@/components/participant/modal/EventFormModal.vue"
  );
const SupervisionFilters = () =>
  import(
    /* webpackChunkName: "supervisionfilters" */ "@/components/supervision/SupervisionFilters.vue"
  );
const ServiceEventModal = () =>
  import(
    /* webpackChunkName: "serviceEvent" */ "@/components/participant/modal/ServiceEventModal.vue"
  );
const ChooseUserModal = () =>
  import(
    /* webpackChunkName: "chooseUserModal" */ "@/components/participant/modal/ChooseUserModal.vue"
  );
const NotificationModal = () =>
  import(
    /* webpackChunkName: "notif" */ "@/components/participant/modal/NotificationModal.vue"
  );

export default {
  name: "home",
  setup() {
    const { data: notificationCount } = useInfoNotificationsCountQuery();

    useCritNotificationsQuery();

    return { notificationCount };
  },
  components: {
    SilaeExportModal,
    EventFormModal,
    CounterModal,
    WeightModal,
    EventListContainer,
    SupervisionFilters,
    ServiceEventModal,
    OisoExportModal,
    NotificationModal,
    ChooseUserModal,
    DailyDistanceExportModal,
    BusinessInfoModal,
  },
  data() {
    return {
      drawer: true,
      topMenuItems: [],
      apiUrl: process.env.VUE_APP_KRONOS_API,
      tab: null,
    };
  },
  computed: {
    ...mapState({
      customerFullName: (state) => state.customer.fullName,
      customerId: (state) => state.customer.id,
      customerRoles: (state) => state.customer.roles,
      token: (state) => state.security.token,
      selectedTimeSlot: "selectedTimeSlot",
      calendarOwner: "calendarOwner",
      impersonate: "impersonate",
    }),
    ...mapGetters({
      impersonationRedirectPath: [getters.GET_IMPERSONATION_REDIRECT_PATH],
    }),
    hasDrawer: function () {
      return ["calendar", "supervision", "calendar-week"].includes(
        this.$route.name
      );
    },
    // hasDrawerSupervision: function() {
    //   return ["supervision"].includes(this.$route.name);
    // },
    drawerComponent: function () {
      if ("supervision" === this.$route.name) {
        return "SupervisionFilters";
      }

      return "EventListContainer";
    },
    menuItems: function () {
      let items = [];

      if (!this.customerRoles) {
        return [];
      }
      if (this.hasCalendar()) {
        items.push({
          path: "/calendar",
          icon: "mdi-calendar",
          name: this.$i18n.t("labels.calendar"),
        });
      }

      items.push({
        path: "/supervision",
        icon: "mdi-eye-check",
        name: this.$i18n.t("labels.supervising"),
      });

      if (this.$route.name === "auditor-calendar") {
        items.push({
          path: "/auditor-calendar",
          icon: "mdi-target-account",
          name: this.$i18n.t("labels.calendar_of", {
            owner: this.calendarOwner,
          }),
        });
      }

      return items;
    },
    hasAdminAccess: function () {
      this.initMenuItems();
      return this.customerRoles && this.customerRoles.includes("ROLE_ADMIN");
    },
    hasImpersonateAccess: function () {
      return (
        this.customerRoles &&
        (this.customerRoles.includes("ROLE_GESTION_1") ||
          this.customerRoles.includes("ROLE_GESTION_1"))
      );
    },
  },
  created() {
    EventBus.$on("openSidebar", () => {
      this.drawer = true;
    });
    EventBus.$on("hideSidebar", () => {
      this.drawer = false;
    });

    this.fetchData();

    this.initMenuItems();
  },
  methods: {
    ...mapActions([
      actions.AUTH_LOGOUT,
      actions.UNSET_IMPERSONATE,
      actions.FETCH_DEPARTMENTS,
      actions.FETCH_BUSINESS_GROUPS,
      actions.SET_IMPERSONATION_REDIRECT_PATH,
    ]),
    fetchData() {
      return Promise.allSettled([
        this[actions.FETCH_DEPARTMENTS](),
        this[actions.FETCH_BUSINESS_GROUPS](),
      ]);
    },
    // async changeLocale(locale) {
    //   try {
    //     await loadLocaleMessagesAsync(locale);
    //     setDocumentLang(locale);
    //     this.$i18n.locale = locale;
    //     localStorage.setItem("lang", locale);
    //
    //     const response = await eventApi.test(locale);
    //     console.log(response.data);
    //   } catch (e) {
    //     console.error({ changeLocalError: e });
    //   }
    // },
    fixSelectedTimeSlot() {
      EventBus.$emit("fixSelectedTimeSlot");
    },
    hasCalendar() {
      return this.customerRoles.includes("ROLE_PLANIFICATION");
    },
    folderFrOnly() {
      return this.$i18n.locale !== "pt";
    },

    initMenuItems() {
      this.topMenuItems = [];

      if (this.folderFrOnly()) {
        if (this.customerRoles.includes("ROLE_GESTION_1")) {
          this.topMenuItems.push({
            name: "rh-stat",
            icon: "mdi-chart-tree",
            description: this.$i18n.t("tooltips.exportNDFKM"),
          });
        }
        if (this.customerRoles.includes("ROLE_ADMIN")) {
          this.topMenuItems.push({
            name: "silae",
            icon: "mdi-ghost",
            description: this.$i18n.t("tooltips.exportSilae"),
          });
          this.topMenuItems.push({
            name: "oiso",
            icon: "mdi-owl",
            description: this.$i18n.t("tooltips.exportOISO"),
          });
        }
      }

      if (this.hasCalendar()) {
        this.topMenuItems.push({
          name: "weight",
          icon: "mdi-weight-lifter",
          description: this.$i18n.t("tooltips.calculateLoad"),
        });
        this.topMenuItems.push({
          name: "counter",
          icon: "mdi-timer",
          description: this.$i18n.t("tooltips.consultCredits"),
        });
      }
    },
    async redirectToOrigin() {
      if (this.impersonationRedirectPath) {
        try {
          console.log("redirect to origin", this.impersonationRedirectPath);
          await this.$router.push(this.impersonationRedirectPath);
        } catch (error) {
          console.error(error);
        } finally {
          this[actions.SET_IMPERSONATION_REDIRECT_PATH](null);
        }
      }
    },
    logout: async function () {
      if (this.impersonate) {
        await this[actions.UNSET_IMPERSONATE]();
        await this.redirectToOrigin();
        await this.$router.go(0);

        return;
      }

      this[actions.AUTH_LOGOUT]();
      await this.$router.push("/login");
    },
    editModalChooseUser: function () {
      if (
        this.hasAdminAccess ||
        this.impersonate ||
        this.hasImpersonateAccess
      ) {
        EventBus.$emit("open-choose-user-modal", {
          customerId: this.customerId,
        });
      }
    },
  },
};
</script>
